import { DateTime } from "luxon";
import { operatingDate, type Turn } from "@yb/core";

/**
 * What Vectro Apron's cameras say about a turn (server: apps/server/src/apron-turns.ts,
 * contract docs/APRON_TURN_FEED.md). Shown beside the plan, never instead of it.
 */
export type CameraState = "arriving" | "on_stand" | "leaving" | "empty" | "unknown";

export interface CameraMedia {
  key: string;
  moment: string;
  kind: "still" | "clip";
  contentType: string;
  size: number;
  sha256: string;
  capturedAt: string | null;
  camera: string | null;
  caption: string | null;
  receivedAt: string;
  url: string;
}

export interface CameraTurn {
  turnId: string;
  revision: number;
  at: string;
  stand: string | null;
  standId: string | null;
  standName: string | null;
  date: string;
  arrival: { flightNumber: string; scheduledAt: string | null } | null;
  departure: { flightNumber: string; scheduledAt: string | null } | null;
  airlines: string[];
  state: CameraState;
  stateSince: string | null;
  onBlockAt: string | null;
  offBlockAt: string | null;
  registration: { schedule: string | null; camera: string | null; status: "match" | "mismatch" | "reading" | "unreadable" | "none"; confirmedAt: string | null; confidence?: number | null } | null;
  steps: Array<{ key: string; label: string; start: string | null; end: string | null }>;
  prediction: { expectedPushbackAt: string | null; risk: "on_track" | "warning" | "critical" | null } | null;
  conflict: { type: string; summary: string; since: string | null } | null;
  receivedAt: string;
  updatedAt: string;
  media: CameraMedia[];
}

/** A live turn with no revision for this long is called out: Apron sends one every 10 min. */
export const STALE_AFTER_MIN = 15;

/** The state as a planner says it. "unknown" is never "empty": the cameras cannot tell. */
export function stateWord(s: CameraState): string {
  return { arriving: "Arriving", on_stand: "On stand", leaving: "Leaving", empty: "Stand empty", unknown: "Cameras unsure" }[s];
}

/** Colour of the state chip, from the app's own tones. */
export function stateTone(s: CameraState): "ok" | "info" | "warn" | "off" {
  return s === "on_stand" ? "ok" : s === "arriving" ? "info" : s === "leaving" ? "warn" : "off";
}

export const isLive = (t: CameraTurn) => t.state !== "empty";

/** Minutes since the last revision of a live turn, when that is past the stale mark; otherwise null. */
export function staleMinutes(t: CameraTurn, now: number): number | null {
  if (!isLive(t)) return null;
  const min = Math.floor((now - Date.parse(t.at)) / 60_000);
  return min > STALE_AFTER_MIN ? min : null;
}

/** The registration check in words, with its tone. Null when there is nothing to say. */
export function registrationText(r: CameraTurn["registration"]): { text: string; tone: "ok" | "bad" | "warn" | "off" } | null {
  if (!r || r.status === "none") return null;
  switch (r.status) {
    case "match":
      return { text: `${r.camera ?? r.schedule ?? "Registration"} matches the schedule`, tone: "ok" };
    case "mismatch":
      return { text: `Cameras read ${r.camera ?? "another registration"}, the schedule says ${r.schedule ?? "nothing"}`, tone: "bad" };
    case "reading":
      return { text: "Reading the registration…", tone: "off" };
    case "unreadable":
      return { text: "The registration could not be read", tone: "warn" };
  }
}

/**
 * The tail check: what the cameras read against what the airport's AODB says, with the tail
 * crop that produced the read as its proof. Apron compares against a schedule of its own;
 * this compares against the AODB's tail on the flight here, so a wrong tail in either system
 * shows, and the two answers are never mixed up.
 */
export interface TailCheck {
  verdict: "verified" | "mismatch" | "unconfirmed" | "camera_only";
  tone: "ok" | "bad" | "warn" | "off" | "info";
  text: string;
  /** The AODB's tail for the flight: the arrival's, or the departure's when there is none. */
  aodb: string | null;
  camera: string | null;
  /** What Apron compared against, when it differs from the AODB's. */
  apronSchedule: string | null;
  confidence: number | null;
  confirmedAt: string | null;
  /** The tail crop that confirmed the read. */
  proof: CameraMedia | null;
}

/** C-FENO, CFENO and "c-feno " are one tail. */
export const sameTail = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.toUpperCase().replace(/[^A-Z0-9]/g, "") === b.toUpperCase().replace(/[^A-Z0-9]/g, "");

export function tailCheck(cam: CameraTurn, turn: Turn): TailCheck | null {
  const r = cam.registration;
  const aodb = turn.arr?.registration?.trim() || turn.dep?.registration?.trim() || null;
  const camera = r && r.status !== "reading" && r.status !== "unreadable" ? r.camera?.trim() || null : null;
  if (!camera && (!r || r.status === "none")) return null;
  const proof = cam.media.find((m) => m.moment === "registration" && m.kind === "still") ?? null;
  const base = { aodb, camera, apronSchedule: r?.schedule && !sameTail(r.schedule, aodb) ? r.schedule : null, confidence: r?.confidence ?? null, confirmedAt: r?.confirmedAt ?? null, proof };
  if (!camera) {
    return r?.status === "unreadable"
      ? { ...base, verdict: "unconfirmed", tone: "warn", text: `The cameras could not read the tail${aodb ? `; the AODB says ${aodb}` : ""}` }
      : { ...base, verdict: "unconfirmed", tone: "off", text: "Reading the tail…" };
  }
  if (!aodb) return { ...base, verdict: "camera_only", tone: "info", text: `Cameras read ${camera}; the AODB has no tail for this flight` };
  return sameTail(camera, aodb)
    ? { ...base, verdict: "verified", tone: "ok", text: `${aodb} verified: the cameras read the tail the AODB has` }
    : { ...base, verdict: "mismatch", tone: "bad", text: `Wrong tail: cameras read ${camera}, the AODB says ${aodb}` };
}

/** The check as plain text, to paste into an email or a report as the record of it. */
export function tailProofText(check: TailCheck, cam: CameraTurn, turn: Turn, tz: string): string {
  const flights = [turn.arr?.flightNumber, turn.dep?.flightNumber].filter(Boolean).join("/");
  const local = (iso: string | null) => (iso ? `${DateTime.fromISO(iso).setZone(tz).toFormat("yyyy-MM-dd HH:mm:ss")} local (${iso})` : "not given");
  const lines = [
    `Tail check ${flights} on ${cam.date}, ${cam.standName ?? (cam.stand ? `gate ${cam.stand}` : "stand unknown")}`,
    `Result: ${check.verdict === "verified" ? "VERIFIED" : check.verdict === "mismatch" ? "MISMATCH" : check.verdict === "camera_only" ? "CAMERA READ ONLY (no AODB tail)" : "NOT CONFIRMED"}`,
    `AODB tail: ${check.aodb ?? "none"}`,
    `Cameras read: ${check.camera ?? "nothing confirmed"}${check.confidence != null ? ` (confidence ${Math.round(check.confidence * 100)}%)` : ""}`,
    ...(check.apronSchedule ? [`Apron compared against: ${check.apronSchedule}`] : []),
    `Read confirmed: ${local(check.confirmedAt)}`,
    check.proof
      ? `Proof: tail crop captured ${local(check.proof.capturedAt)}${check.proof.camera ? ` by camera ${check.proof.camera}` : ""}, SHA-256 ${check.proof.sha256}`
      : "Proof: no tail crop received from Vectro Apron",
    `Source: Vectro Apron turn ${cam.turnId}, revision ${cam.revision}`,
  ];
  return lines.join("\n");
}

const MOMENTS: Record<string, string> = {
  on_block: "On block",
  registration: "Registration",
  belt_loader_attached: "Belt loader",
  off_block: "Off block",
};
const MOMENT_ORDER = Object.keys(MOMENTS);

/** A moment's name on screen; one Apron adds later reads from its key. */
export function momentLabel(moment: string): string {
  return MOMENTS[moment] ?? moment.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/** The key moments of a turn, in the order they happen, each with its still and its clip. */
export function moments(media: CameraMedia[]): Array<{ moment: string; still: CameraMedia | null; clip: CameraMedia | null }> {
  const by = new Map<string, { moment: string; still: CameraMedia | null; clip: CameraMedia | null }>();
  for (const m of media) {
    const e = by.get(m.moment) ?? { moment: m.moment, still: null, clip: null };
    e[m.kind] = m;
    by.set(m.moment, e);
  }
  const rank = (m: string) => (MOMENT_ORDER.includes(m) ? MOMENT_ORDER.indexOf(m) : MOMENT_ORDER.length);
  return [...by.values()].sort((a, b) => rank(a.moment) - rank(b.moment) || a.moment.localeCompare(b.moment));
}

const legDay = (l: { scheduledAt: string | null } | null, fallback: string, tz: string) => (l?.scheduledAt ? operatingDate(Date.parse(l.scheduledAt), tz) : fallback);

/** The camera turns by "flight|operating date|direction", for finding a board turn's own. */
export function indexCameraTurns(list: CameraTurn[], tz: string): Map<string, CameraTurn> {
  const m = new Map<string, CameraTurn>();
  // Oldest first, so where two claim a flight the newest revision is the one kept.
  for (const t of [...list].sort((a, b) => a.at.localeCompare(b.at))) {
    if (t.arrival) m.set(`${t.arrival.flightNumber}|${legDay(t.arrival, t.date, tz)}|ARR`, t);
    if (t.departure) m.set(`${t.departure.flightNumber}|${legDay(t.departure, t.date, tz)}|DEP`, t);
  }
  return m;
}

/** The camera turn of a board turn: its arrival, or failing that its departure, on the leg's own day. */
export function cameraTurnFor(t: Turn, index: Map<string, CameraTurn>, tz: string): CameraTurn | null {
  const arr = t.arr ? index.get(`${t.arr.flightNumber}|${operatingDate(t.arr.scheduled, tz)}|ARR`) : undefined;
  const dep = t.dep ? index.get(`${t.dep.flightNumber}|${operatingDate(t.dep.scheduled, tz)}|DEP`) : undefined;
  return arr ?? dep ?? null;
}

/**
 * The proven steps laid out on one time line, from the first start to the last end (or
 * now, while a step is still going): left and width in percent of that span.
 */
export function stepBars(steps: CameraTurn["steps"], now: number): { from: number; to: number; bars: Array<{ key: string; label: string; left: number; width: number; open: boolean; start: number; end: number }> } | null {
  const timed = steps.filter((s) => s.start);
  if (!timed.length) return null;
  const starts = timed.map((s) => Date.parse(s.start!));
  const ends = timed.map((s) => (s.end ? Date.parse(s.end) : Math.max(now, Date.parse(s.start!))));
  const from = Math.min(...starts);
  const to = Math.max(...ends, from + 60_000);
  const span = to - from;
  return {
    from,
    to,
    bars: timed.map((s, i) => ({
      key: s.key,
      label: s.label || momentLabel(s.key),
      left: ((starts[i]! - from) / span) * 100,
      width: Math.max(1.5, ((ends[i]! - starts[i]!) / span) * 100),
      open: !s.end,
      start: starts[i]!,
      end: ends[i]!,
    })),
  };
}
